import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type { ApiInfrastructureErrors } from '../../../data/error/api';
import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { ProductFavouriteListWithoutProducts } from '@vs-shop/schemas/branded/MiscSchema';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/product-favorites/list/{uuid}/products';
const HTTP_METHOD = 'post';

export const AddProductsToFavouriteListError = createBackendApiError({
  domain: 'ProductFavouriteList',
  endpoint: 'AddProducts',
  path: ENDPOINT,
  identifierField: 'uuid',
} as const);

export type AddProductsToFavouriteListError = BackendApiErrorUnion<
  typeof AddProductsToFavouriteListError
>;

export type AddProductsToFavouriteListInput = {
  uuid: string;
  productVariantCodes: string[];
};

const postAddProductsToFavouriteListFetch = (data: AddProductsToFavouriteListInput) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path('/api/v2/shop/product-favorites/list/{uuid}/products')
      .method(HTTP_METHOD)
      .accept('application/ld+json', 'application/json')
      .fetch({
        params: { uuid: data.uuid },
        body: { productVariantCodes: data.productVariantCodes as [string, ...string[]] },
      });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'productFavouriteList.uuid': data.uuid,
      },
    })
  );

const addProductsToFavouriteList = (input: AddProductsToFavouriteListInput) =>
  postAddProductsToFavouriteListFetch(input).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': (response) => schemaBodyJsonClean(ProductFavouriteListWithoutProducts)(response),
        404: (response) =>
          Effect.fail(
            new AddProductsToFavouriteListError.NotFoundError({ uuid: input.uuid, response })
          ),
        422: AddProductsToFavouriteListError.createViolationError(input.uuid),
        orElse: (response) =>
          Effect.fail(
            new AddProductsToFavouriteListError.ApiRequestError({ uuid: input.uuid, response })
          ),
      })
    ),
    Effect.tap((result) =>
      logApiOperation(
        ApiModule.ProductFavouriteListProductsAdd,
        'Debug',
        `Successfully added products to favourite list: ${result.name}`
      )
    ),
    Effect.withSpan('api.productFavouriteList.addProducts', {
      kind: 'internal',
      attributes: {
        'productFavouriteList.uuid': input.uuid,
      },
    })
  );

type AddProductsToFavouriteListRequest = {
  readonly _tag: 'AddProductsToFavouriteListRequest';
  readonly uuid: string;
  readonly productVariantCodes: string[];
} & Request.Request<
  ProductFavouriteListWithoutProducts,
  AddProductsToFavouriteListError | ApiInfrastructureErrors,
  BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
>;

const AddProductsToFavouriteListRequest = Request.tagged<AddProductsToFavouriteListRequest>(
  'AddProductsToFavouriteListRequest'
);

const AddProductsToFavouriteListResolver = RequestResolver.fromEffect(
  liftRequest((request: AddProductsToFavouriteListRequest) =>
    addProductsToFavouriteList({
      uuid: request.uuid,
      productVariantCodes: request.productVariantCodes,
    })
  )
);

export const requestAddProductsToFavouriteList = (input: AddProductsToFavouriteListInput) =>
  Effect.request(
    AddProductsToFavouriteListRequest({
      uuid: input.uuid,
      productVariantCodes: input.productVariantCodes,
    }),
    AddProductsToFavouriteListResolver
  );
